import { useMemo } from 'react';

import { EventDate, useInfiniteListEventDates } from '@givebacks/api/store';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { CalendarDate, NextPageLoader, SelectableCard, Stack, Text, Title } from 'ui';

import { QuantityBadge } from './QuantityBadge';
import { useGetInventoryStatus } from './useGetInventoryStatus';

interface EventDateCardProps {
  eventDate: EventDate;
  showInventoryStatus?: boolean;
  onClick?: () => void;
}

const EventDateCard = ({ eventDate, showInventoryStatus = true, onClick }: EventDateCardProps) => {
  const startTime = DateTime.fromISO(eventDate.starting_at)
    .toLocaleString(DateTime.TIME_SIMPLE)
    .replace(' AM', 'am')
    .replace(' PM', 'pm');
  const endTime = DateTime.fromISO(eventDate.ending_at)
    .toLocaleString(DateTime.TIME_SIMPLE)
    .replace(' AM', 'am')
    .replace(' PM', 'pm');
  const timezone = DateTime.fromISO(eventDate.starting_at).toFormat('ZZZZ');

  const inventoryStatus = useGetInventoryStatus(
    eventDate.event_date_tickets,
    eventDate.inventory?.remaining_capacity
  );

  return (
    <SelectableCard
      title={eventDate.title}
      titleClamp={1}
      description={
        <Text fz="sm" lineClamp={1} ta="left">{`${startTime} - ${endTime} ${timezone}`}</Text>
      }
      header={<CalendarDate date={eventDate.starting_at} />}
      onClick={onClick}
      detail={
        showInventoryStatus &&
        inventoryStatus && (
          <Stack h="100%" justify="flex-end" style={{ flexShrink: 0 }}>
            <QuantityBadge inventoryStatus={inventoryStatus} />
          </Stack>
        )
      }
    />
  );
};

interface CalendarSectionProps {
  causeId?: string;
  eventId?: string;
}

const CalendarSection = ({ eventId, causeId }: CalendarSectionProps) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteListEventDates({
    eventId,
    causeId,
    queryOptions: {
      options: {
        include: 'event_date_tickets,inventory',
      },
      order: {
        field: 'starting_at',
        direction: 'asc',
      },
    },
  });

  const navigate = useNavigate();

  /**
   * This will filter out past events and sort the events by date.
   * If there are no future events, it will return null.
   */
  const eventDates = useMemo(() => {
    if (!data) return [];
    const _eventDates = data?.pages?.flatMap((page) => page.event_dates) ?? [];

    const sortedEventDates = _eventDates
      .sort((firstEventDate, secondEventDate) => {
        const dateA = DateTime.fromISO(firstEventDate.starting_at);
        const dateB = DateTime.fromISO(secondEventDate.starting_at);
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
      })
      .slice();

    const today = DateTime.now().startOf('day');
    const currentAndFutureEvents = sortedEventDates.findIndex((eventDate) => {
      return DateTime.fromISO(eventDate.starting_at).startOf('day') >= today;
    });

    if (currentAndFutureEvents === -1) return null;

    const futureEventDates = _eventDates.slice(currentAndFutureEvents);

    return futureEventDates.map((eventDate) => {
      return (
        <EventDateCard
          key={eventDate.uuid}
          eventDate={eventDate}
          onClick={() => navigate(`event-date/${eventDate.uuid}`)}
        />
      );
    });
  }, [causeId, data, eventId, navigate]);

  if (!eventDates) return null;

  return (
    <Stack>
      <Title order={2}>Calendar</Title>
      <Stack>
        {eventDates}
        <NextPageLoader
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          handleFetchNextPage={() => void fetchNextPage()}
        />
      </Stack>
    </Stack>
  );
};

export { CalendarSection, EventDateCard };
